import { STAFF_CATALOG_VERIFICATION_STALE_DAYS } from "@lafayette/schemas";
import { useState } from "react";
import { Button } from "../../ui/button";
import { SectionCard } from "../../ui/section-card";
import { StatusBadge } from "../../ui/status-badge";
import { StatusMessage } from "../../ui/status-message";
import { describeOutcome, type SaveMessage } from "../commands";
import { formatDateTime, verificationFilterLabels, verificationFreshness } from "../labels";
import type { ItemSectionContext } from "./section-support";

/** When a person last checked the item against the real world, and a button to record a new check. */
export function VerificationStamp({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, command, now } = ctx;
  const { item } = detail;
  const [message, setMessage] = useState<SaveMessage | null>(null);
  const freshness = verificationFreshness(item.lastVerifiedAt, now);

  async function verify() {
    setMessage(null);
    const outcome = await command.run("markVerified", { expectedVersion: item.version });
    setMessage(describeOutcome(outcome, "Recorded as verified now."));
  }

  return (
    <SectionCard
      id="verification"
      title="Verification"
      description={`An item not verified for more than ${STAFF_CATALOG_VERIFICATION_STALE_DAYS} days counts as stale.`}
    >
      <p className="m-0 flex flex-wrap items-center gap-2">
        <StatusBadge tone={freshness === "fresh" ? "success" : "warning"}>
          {freshness === "fresh" ? "Fresh" : verificationFilterLabels[freshness]}
        </StatusBadge>
        {item.lastVerifiedAt === null
          ? "Nobody has verified this item yet."
          : `Last verified ${formatDateTime(item.lastVerifiedAt)}.`}
      </p>
      {readOnly ? null : (
        <div>
          <Button type="button" onClick={() => void verify()} disabled={command.pending}>
            Mark as verified now
          </Button>
        </div>
      )}
      <StatusMessage message={message} />
    </SectionCard>
  );
}
